import { Model, Factory, belongsTo, trait, hasMany } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randBoolean,
  randProductName,
  randNumber,
  randPastDate,
  randRecentDate,
  randFutureDate,
  randProductDescription,
  randUuid,
} from '@ngneat/falso'
import type { SchemaAttributes } from '../types.js'
import type { MirageServer, MockServer } from './server.js'
import { generateFinanceAmount } from './utils.js'
import { roundEven, toNumber } from '@/utils/number.js'
import {
  HealthPlanStatuses,
  HealthPlanSubscriptionStatuses,
} from '@/composables/healthPlans.js'

export type HealthPlanData = SchemaAttributes<'HealthPlan'>

export const model = Model.extend({
  department: belongsTo('department'),
  additionalDepartments: hasMany('department'),
  species: belongsTo('listItem'),
  invoicingItem: belongsTo('item'),
  compensationItem: belongsTo('item'),
  cancellationFeeItem: belongsTo('item'),
  defaultRenewalPlan: belongsTo('healthPlan', { inverse: null }),
  healthPlanItems: hasMany('healthPlanItem'),
  itemGroups: hasMany('healthPlanItemGroup'),
  client: belongsTo('client'),
  patient: belongsTo('patient'),
}) as ModelDefinition<HealthPlanData>

export const factory = Factory.extend<HealthPlanData>({
  display_name() {
    return `${randProductName()} Plan`
  },
  internal_name() {
    return String(this.display_name).toLowerCase().replace(/\s+/g, '_')
  },
  status() {
    return rand(HealthPlanStatuses)
  },
  subscription_status() {
    return rand(HealthPlanSubscriptionStatuses)
  },
  months() {
    return randNumber({ min: 6, max: 24 })
  },
  monthly_fee() {
    return roundEven(toNumber(generateFinanceAmount({ min: 25, max: 500 })), 2)
  },
  initial_payment() {
    return roundEven(toNumber(generateFinanceAmount({ min: 25, max: 500 })), 2)
  },
  created_at() {
    return randPastDate().toISOString()
  },
  modified() {
    return randRecentDate().toISOString()
  },
  transferable() {
    return randBoolean()
  },
  description() {
    return randProductDescription()
  },
  weight_min() {
    return randNumber({ min: 0.5, max: 80, fraction: 2 })
  },
  weight_max() {
    return randNumber({ min: this.weight_min as number, max: 100, fraction: 2 })
  },
  age_min() {
    return randNumber({ min: 1, max: 80 })
  },
  age_max() {
    return randNumber({ min: this.age_min as number, max: 100 })
  },
  ongoing() {
    return randBoolean()
  },
  validity_start() {
    return rand([null, randPastDate().toISOString()])
  },
  validity_end() {
    return rand([null, randFutureDate().toISOString()])
  },
  internal_notes() {
    return randProductDescription()
  },
  external_id() {
    return randUuid()
  },

  afterCreate(healthPlan, server) {
    if (!healthPlan.client) {
      healthPlan.update({
        client: server.create('client', 'withSecondaryOwner'),
      })
    }
    if (!healthPlan.patient) {
      healthPlan.update({ patient: server.create('patient') })
    }
  },

  withDefaultRenewalPlan: trait({
    afterCreate(healthPlan, server) {
      healthPlan.update({
        defaultRenewalPlan: server.create('healthPlan', 'withSpecies'),
      })
    },
  }),

  withSpecies: trait({
    afterCreate(healthPlan, server) {
      healthPlan.update({ species: server.create('listItem', 'asSpecies') })
    },
  }),
})

export function seeds(server: MockServer) {
  server.createList('healthPlan', 10, 'withSpecies')
}

export function routes(server: MockServer) {
  server.get('/health_plans', function (this: MirageServer, schema) {
    return schema.all('healthPlan')
  })
}
